const stores = new Set();
const listeners = new Set();

function emit(type, details = {}) {
    const event = Object.freeze({ type, timestamp: Date.now(), ...details });
    let firstError;

    for (const listener of Array.from(listeners)) {
        if (!listeners.has(listener)) continue;
        try {
            listener(event);
        } catch (error) {
            firstError ??= error;
        }
    }

    if (firstError) throw firstError;
}

export function registerStore(store) {
    stores.add(store);
    store.__removeFromRegistry = () => {
        if (!stores.delete(store)) return;
        emit('unregister', { store });
    };
    try {
        emit('register', { store });
    } catch (error) {
        stores.delete(store);
        delete store.__removeFromRegistry;
        throw error;
    }
    return store;
}

function persistentStores() {
    return Array.from(stores).filter((store) => store.kind === 'persistent');
}

async function runAll(type, selectedStores, operation) {
    emit(`${type}:start`, { stores: selectedStores });
    try {
        const results = await Promise.all(selectedStores.map(operation));
        emit(`${type}:complete`, { stores: selectedStores, results });
        return results;
    } catch (error) {
        emit(`${type}:error`, { stores: selectedStores, error });
        throw error;
    }
}

export const restateManager = Object.freeze({
    list() {
        return Array.from(stores);
    },

    get(id) {
        return Array.from(stores).find((store) => store.id === id || store.key === id);
    },

    size() {
        return stores.size;
    },

    subscribe(listener) {
        if (typeof listener !== 'function') {
            throw new TypeError('The manager listener must be a function.');
        }
        listeners.add(listener);
        let active = true;
        return () => {
            if (!active) return;
            active = false;
            listeners.delete(listener);
        };
    },

    resetAll() {
        const selectedStores = Array.from(stores);
        const values = selectedStores.map((store) => store.reset());
        emit('resetAll', { stores: selectedStores, values });
        return values;
    },

    saveAll() {
        const selectedStores = persistentStores();
        return runAll('saveAll', selectedStores, (store) => store.save());
    },

    restoreAll() {
        const selectedStores = persistentStores();
        return runAll('restoreAll', selectedStores, (store) => store.restore());
    },

    clearStorageAll() {
        const selectedStores = persistentStores();
        return runAll('clearStorageAll', selectedStores, (store) => store.clearStorage());
    },

    resetAllAndClearStorage() {
        const selectedStores = Array.from(stores);
        return runAll('resetAllAndClearStorage', selectedStores, (store) =>
            store.kind === 'persistent' ? store.resetAll() : Promise.resolve(store.reset())
        );
    },

    destroyAll() {
        const selectedStores = Array.from(stores);
        selectedStores.forEach((store) => store.destroy());
        emit('destroyAll', { stores: selectedStores });
    },
});
